import React, { useEffect, useMemo, useState } from 'react';
import { Popover } from 'antd';
import { useAxios } from 'hooks/useAxios';
import { BASE_URL_V2 } from 'shared/constants';
import {
  createDeliverablesApi,
  DeliverableViewerModal,
  type DeliverableListItemDto,
  type DeliverablesApiClient
} from 'components/DealDeliverables';
import { CalendarReportsBadge } from './CalendarReportsBadge';
import { CalendarReportsList } from './CalendarReportsList';
import { resolveReportCount } from './reportCount';

/**
 * How long a fetched deliverables list is treated as fresh, in milliseconds.
 *
 * `GET /deals/:dealId/deliverables` embeds a presigned S3 URL per document, presigned
 * server-side with `expiresIn: 3600` (one hour — see `deliverable.service.ts`). Both popover
 * actions consume that embedded URL (Open feeds the viewer, Download opens it in a tab), so a
 * list cached past the presign window yields a viewer error and an S3 403. This cell's React
 * portal is keyed by record + column and only torn down when the record's `generation` changes,
 * so on a calendar tab left open its state can outlive the hour by a wide margin — hence a TTL
 * rather than a fetch-once flag. 15 minutes leaves at least 45 minutes of presign validity on
 * any URL a user can click, while still coalescing rapid close/reopen cycles into one request.
 */
const REPORTS_FRESHNESS_TTL_MS = 15 * 60 * 1000;

/** Stable empty list handed to `CalendarReportsList` while a fetch is outstanding. */
const NO_REPORTS: DeliverableListItemDto[] = [];

/**
 * Marker class on the popover overlay. The dismiss-on-scroll listener uses it to tell a scroll
 * of the page apart from a scroll of the popover's OWN report list, which must not dismiss it.
 */
const REPORTS_POPOVER_CLASS = 'calendar-reports-popover';

export interface CalendarReportsCellProps {
  /** The deal whose deliverables the popover lazy-fetches on open. */
  dealId: string;
  /** Active deliverable count for the deal; drives the badge and gates the Popover entirely. */
  count: number | null | undefined;
  /** Injected API client for tests; defaults to the V2 axios-backed implementation. */
  api?: DeliverablesApiClient;
}

/**
 * Smart Calendar™ "Report(s)" cell: hosts the count badge, a click-triggered antd
 * `Popover` listing the deal's deliverables, and the `DeliverableViewerModal` the
 * popover's Open action drives. The deliverables list is lazy-fetched on popover open —
 * never on mount — and then reused for `REPORTS_FRESHNESS_TTL_MS`, so closing and
 * reopening the popover does not re-request, but a popover opened after the cached rows
 * have gone stale re-fetches and gets freshly presigned document URLs. A deal with no
 * active deliverables renders only the em-dash badge with no `Popover` wrapper, so an
 * empty cell has no click target and never fetches.
 * @param {CalendarReportsCellProps} props - Cell inputs.
 * @returns {React.ReactElement} The badge alone, or the badge + popover + viewer modal.
 */
export function CalendarReportsCell({ dealId, count, api: apiProp }: CalendarReportsCellProps): React.ReactElement {
  const { axios } = useAxios(BASE_URL_V2);
  const api = useMemo(() => apiProp ?? createDeliverablesApi(axios), [apiProp, axios]);

  const [visible, setVisible] = useState(false);
  // `Date.now()` when the current `reports` were REQUESTED (not when they resolved): the presign
  // clock starts server-side at request time, so stamping on request is the conservative read of
  // freshness, and it also means a close/reopen while the request is still in flight sees a fresh
  // stamp and does not fire a duplicate request. Reset to null on failure so a reopen retries.
  const [requestedAt, setRequestedAt] = useState<number | null>(null);
  // Whether the fetch effect has already evaluated the CURRENT open. Cleared on every close, so
  // each open gets exactly one freshness evaluation. Distinct from `requestedAt`, which survives
  // closes — this is what lets `pending` (below) be derived synchronously.
  const [evaluated, setEvaluated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState<DeliverableListItemDto[]>([]);
  const [activeDeliverable, setActiveDeliverable] = useState<DeliverableListItemDto | null>(null);

  const stale = requestedAt == null || Date.now() - requestedAt >= REPORTS_FRESHNESS_TTL_MS;

  /**
   * Drives the popover's open state and resets the per-open fetch evaluation on close.
   * Every close path must run through here — an `evaluated` flag left set across a close
   * would stop the next open from re-checking freshness (or retrying after a failure).
   * Declared above the `count <= 0` early return so the dismiss-on-scroll effect can use it.
   * @param {boolean} nextVisible - Whether the popover should now be open.
   * @returns {void}
   */
  const setPopoverVisible = (nextVisible: boolean): void => {
    setVisible(nextVisible);
    if (!nextVisible) setEvaluated(false);
  };

  useEffect(() => {
    if (!visible || evaluated) return;
    setEvaluated(true);
    if (!stale) return;
    setLoading(true);
    setRequestedAt(Date.now());
    api
      .getDeliverables(dealId)
      .then((result) => {
        setReports(result ?? []);
        setLoading(false);
        // A nullish result IS the live failure path, not an edge case: the shared `useAxios`
        // response interceptor converts rejections into *fulfilled* promises carrying the
        // `AxiosError`, so `res.data` is `undefined` and `getDeliverables` returns `undefined`
        // through its SUCCESS path — `.catch` below never fires today. Clearing the freshness
        // stamp here is therefore what actually makes a failed fetch retryable; without it the
        // cell would serve an empty list for the rest of the TTL and the user would have no way
        // to reach the reports short of waiting 15 minutes. A genuinely empty deal resolves `[]`,
        // which is a real, cacheable answer — hence `== null` rather than a falsy check.
        if (result == null) setRequestedAt(null);
      })
      .catch(() => {
        // Same handling as a nullish resolve above, for the day the interceptor stops swallowing
        // rejections. No toast: the interceptor already surfaces one, and a second is worse than
        // none. Unreachable today — see the `result == null` comment for why.
        setReports([]);
        setLoading(false);
        setRequestedAt(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, evaluated, stale, dealId]);

  // Keep the popover from drifting away from its badge on scroll.
  //
  // antd renders the overlay into `document.body` and only repositions it on window resize —
  // but this app never scrolls the window: `MainLayout` wraps everything in a `!h-screen
  // !overflow-hidden` Layout and scrolls `Layout.Content` instead, while Bryntum scrolls its
  // columns inside its own subgrid scroller. Neither produces a window resize, so an open
  // popover keeps its original body coordinates and separates from the row it belongs to.
  // Dismissing on scroll is the resolution the design handoff specified for this column, and it
  // is also the only one that stays correct for a grid that owns its own scrollers. Scroll
  // events do not bubble, but they DO capture — so a single capture-phase listener on `document`
  // covers every scroller involved without this cell needing a reference to any of them.
  useEffect(() => {
    if (!visible) return undefined;
    const dismissOnScroll = (event: Event): void => {
      // Scrolling the popover's own report list must not close it. A scroll whose target is the
      // document has no `closest`, hence the optional calls.
      const target = event.target as HTMLElement | null;
      if (target?.closest?.(`.${REPORTS_POPOVER_CLASS}`)) return;
      setPopoverVisible(false);
    };
    document.addEventListener('scroll', dismissOnScroll, true);
    return () => document.removeEventListener('scroll', dismissOnScroll, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const n = resolveReportCount(count);
  if (n <= 0) {
    return <CalendarReportsBadge count={count} />;
  }

  // `loading` alone is not enough: it's only set *inside* the fetch effect, which — being a
  // plain `useEffect` — commits and paints one frame after `visible` flips true (a real gap in
  // a browser; `useLayoutEffect` would close it, but `loading` still wouldn't fire until the
  // request is actually kicked off). `pending` is derived synchronously, in the SAME render as
  // the click that sets `visible`, so there is no such gap: the instant the popover opens onto
  // stale (or absent) rows, `pending` is already true. It stays true until the effect's guard
  // (`evaluated`) flips, by which point `loading` has taken over for the remainder of the
  // in-flight request. The `stale` term keeps a within-TTL reopen from flashing a skeleton over
  // rows that are already cached and about to be rendered unchanged.
  const pending = visible && stale && !evaluated;
  const effectiveLoading = loading || pending;

  const handleOpen = (deliverable: DeliverableListItemDto): void => {
    setActiveDeliverable(deliverable);
    setPopoverVisible(false);
  };

  return (
    <>
      <Popover
        trigger="click"
        placement="bottom"
        arrowPointAtCenter
        destroyTooltipOnHide
        visible={visible}
        onVisibleChange={setPopoverVisible}
        overlayClassName={`${REPORTS_POPOVER_CLASS} [&_.ant-popover-inner-content]:!p-0 [&_.ant-popover-inner]:!rounded-lg [&_.ant-popover-inner]:!overflow-hidden`}
        content={
          <CalendarReportsList
            reports={effectiveLoading ? NO_REPORTS : reports}
            // While loading there are no rows to count, so the header count comes from the
            // calendar payload instead. Passing a number — rather than placeholder `reports`
            // entries — keeps the list component free of any obligation to check `loading`
            // before it iterates `reports`.
            headerCount={effectiveLoading ? n : undefined}
            loading={effectiveLoading}
            onOpen={handleOpen}
          />
        }
      >
        {/*
         * antd's `Popover` (rc-trigger) wires `trigger="click"` by cloning its direct child
         * with an injected `onClick` prop. `CalendarReportsBadge` deliberately ignores unknown
         * props (it renders a plain `<button>` with no passthrough — Task 2), so the click
         * handler must land on this intrinsic `<span>` instead; the inner button click still
         * bubbles up to it via normal DOM event propagation. `aria-haspopup`/`aria-expanded`
         * live here rather than on the badge for the same reason.
         */}
        <span className="inline-flex" aria-haspopup="dialog" aria-expanded={visible}>
          <CalendarReportsBadge count={count} active={visible} />
        </span>
      </Popover>
      <DeliverableViewerModal
        deliverable={activeDeliverable}
        open={activeDeliverable != null}
        onClose={() => setActiveDeliverable(null)}
      />
    </>
  );
}
